import React, { useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import { Attribution, TransparentErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';

import { LazyEditorPageCoverPicture } from '../lazyLoadables';
import type { EditorPageCoverPictureEntryComponentProps } from '../pageCoverPictureTypes';
import { CoverPictureStateContainer } from '../CoverPictureStateContainer';

// This component is necessary since we only want to check if isTitleHovered/userOffline for pages without cover photos.
// It determines what props will get passed to EditorCoverPicture and renders it.
// Passing the isTitleHovered prop to pages with cover photos causes an unwanted flicker effect if the title is hovered.
// Similarly if we pass userOffline to a page with a cover picture and the page goes offline,
// the image disappears since it is fetched from the media API but there is no internet, causing the image to return as null
const EditorPageCoverPictureEntryComponent = ({
	contentId,
	isTitleHovered,
	isPublishingContent,
	contentType,
	userOffline,
	coverPictureState,
	isUnpublishedDraft,
	floatingToolbarButtonStyle,
	isFixedWidthImageOption,
	width,
	leftOffset,
	dynamicCoverImageHeight,
	isReadOnly,
	hasCoverPicture,
	aiSuggestedUnsplashImages,
	isUnsplash,
}: EditorPageCoverPictureEntryComponentProps) => {
	// We need this modal state since if there's no cover picture and the modal is open, we don't want to pass the isTitleHovered prop
	// to EditorPageCoverPicture. If passed, this prop causes a bunch of re-renders if the modal is interacted with, making it impossible to
	// use the modal as isTitleHovered keeps getting updated.
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	if (hasCoverPicture) {
		return (
			<div
				data-testid="has-cover-picture-component"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{
					position: isFixedWidthImageOption ? 'relative' : 'absolute',
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview
					left: fg('confluence_frontend_remove_cover_image_left_gap') ? '0px' : `${leftOffset}px`,
				}}
			>
				{/*
        // @ts-ignore */}
				<LazyEditorPageCoverPicture
					contentId={contentId}
					isPublishingContent={isPublishingContent}
					contentType={contentType}
					userOffline={userOffline}
					hasCoverPicture={hasCoverPicture}
					coverPictureState={coverPictureState}
					isUnpublishedDraft={isUnpublishedDraft}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					width={width}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isReadOnly={isReadOnly}
					aiSuggestedUnsplashImages={aiSuggestedUnsplashImages}
					isUnsplash={isUnsplash}
					isFixedWidthImageOption={isFixedWidthImageOption}
				/>
			</div>
		);
	} else if (isModalOpen && !hasCoverPicture) {
		// omit isTitleHovered prop since it causes re-renders if the modal is open and interacted with if the page has no cover picture
		return (
			<div data-testid="no-cover-picture-component-modal-open">
				{/*
        // @ts-ignore */}
				<LazyEditorPageCoverPicture
					contentId={contentId}
					isPublishingContent={isPublishingContent}
					contentType={contentType}
					userOffline={userOffline}
					setIsModalOpen={setIsModalOpen}
					isModalOpen={isModalOpen}
					hasCoverPicture={hasCoverPicture}
					coverPictureState={coverPictureState}
					isUnpublishedDraft={isUnpublishedDraft}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					width={width}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isReadOnly={isReadOnly}
					aiSuggestedUnsplashImages={aiSuggestedUnsplashImages}
				/>
			</div>
		);
	} else {
		// no cover picture and modal isn't open
		return (
			<div data-testid="no-cover-picture-component">
				{/*
        // @ts-ignore */}
				<LazyEditorPageCoverPicture
					contentId={contentId}
					isTitleHovered={isTitleHovered}
					isPublishingContent={isPublishingContent}
					contentType={contentType}
					userOffline={userOffline}
					setIsModalOpen={setIsModalOpen}
					isModalOpen={isModalOpen}
					hasCoverPicture={hasCoverPicture}
					coverPictureState={coverPictureState}
					isUnpublishedDraft={isUnpublishedDraft}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					width={width}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					isReadOnly={isReadOnly}
					aiSuggestedUnsplashImages={aiSuggestedUnsplashImages}
				/>
			</div>
		);
	}
};

export const EditorPageCoverPictureEntry = (props) => (
	<TransparentErrorBoundary attribution={Attribution.PAGE_EXTENSIONS}>
		<Subscribe to={[CoverPictureStateContainer]}>
			{(coverPictureState: CoverPictureStateContainer) => (
				<EditorPageCoverPictureEntryComponent {...props} coverPictureState={coverPictureState} />
			)}
		</Subscribe>
	</TransparentErrorBoundary>
);
